/**
 * Admin → New agent form (Phase 28.10).
 *
 * Creates a new agent via POST /api/admin/agents — always structural,
 * so it opens a PR rather than writing directly to DDB.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createAdminAgent, listAdminAgents } from "../services/adminAgents.js";
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";
import Modal from "../components/Modal.jsx";

const KNOWN_TOOLS = [
  "web_search",
  "search_project_documents",
  "read_project_document",
  "run_research_pipeline",
  "get_pipeline_status",
  "get_cached_outputs",
  "save_document",
  "update_document_section",
];

const EMPTY_FORM = {
  agentId: "",
  name: "",
  promptS3Key: "",
  dependsOn: [],
  outputKey: "",
  maxTokens: 16384,
  toolAllowlist: [],
  enabled: true,
};

export default function AdminAgentsNew() {
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);

  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(() => {
    setLoading(true);
    return listAdminAgents()
      .then((data) => {
        setAllAgents(data.agents || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function toggleDep(depId) {
    setForm((f) => ({
      ...f,
      dependsOn: f.dependsOn.includes(depId)
        ? f.dependsOn.filter((d) => d !== depId)
        : [...f.dependsOn, depId],
    }));
  }

  function toggleTool(tool) {
    setForm((f) => ({
      ...f,
      toolAllowlist: f.toolAllowlist.includes(tool)
        ? f.toolAllowlist.filter((t) => t !== tool)
        : [...f.toolAllowlist, tool],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setShowConfirm(true);
  }

  async function confirmCreate() {
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    try {
      const result = await createAdminAgent(form);
      if (result.prUrl) {
        addToast({ type: "info", message: `PR opened: ${result.prUrl}` });
        navigate("/admin/agents/pending-prs");
      } else {
        addToast({ type: "success", message: "Agent created." });
        navigate("/admin/agents");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSkeleton variant="line" />;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>New Agent</h1>
      </div>

      {error && <ErrorBubble message={error} />}

      <form onSubmit={handleSubmit} className="admin-form">
        <label className="admin-form__field">
          <span>Agent ID</span>
          <input
            type="text"
            value={form.agentId}
            placeholder="agent-10"
            onChange={(e) => setForm((f) => ({ ...f, agentId: e.target.value }))}
            required
            pattern="agent-\d+"
            title="Format: agent-N (e.g. agent-10)"
          />
        </label>

        <label className="admin-form__field">
          <span>Name</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
        </label>

        <label className="admin-form__field">
          <span>Prompt S3 Key</span>
          <input
            type="text"
            value={form.promptS3Key}
            placeholder="prompts/my_agent.md"
            onChange={(e) => setForm((f) => ({ ...f, promptS3Key: e.target.value }))}
            required
          />
        </label>

        <fieldset className="admin-form__fieldset">
          <legend>Depends On</legend>
          {allAgents.length === 0 ? (
            <p className="admin-muted">No agents yet.</p>
          ) : (
            allAgents.map((a) => (
              <label key={a.agentId} className="admin-form__checkbox">
                <input
                  type="checkbox"
                  checked={form.dependsOn.includes(a.agentId)}
                  onChange={() => toggleDep(a.agentId)}
                />
                {a.name} <code>{a.agentId}</code>
              </label>
            ))
          )}
        </fieldset>

        <label className="admin-form__field">
          <span>Output Key</span>
          <input
            type="text"
            value={form.outputKey}
            onChange={(e) => setForm((f) => ({ ...f, outputKey: e.target.value }))}
            required
          />
        </label>

        <label className="admin-form__field">
          <span>Max Tokens</span>
          <input
            type="number"
            value={form.maxTokens}
            min={256}
            max={65536}
            onChange={(e) =>
              setForm((f) => ({ ...f, maxTokens: Number(e.target.value) }))
            }
          />
        </label>

        <fieldset className="admin-form__fieldset">
          <legend>Tool Allowlist</legend>
          {KNOWN_TOOLS.map((tool) => (
            <label key={tool} className="admin-form__checkbox">
              <input
                type="checkbox"
                checked={form.toolAllowlist.includes(tool)}
                onChange={() => toggleTool(tool)}
              />
              {tool}
            </label>
          ))}
        </fieldset>

        <label className="admin-form__field admin-form__field--inline">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          <span>Enabled</span>
        </label>

        <div className="admin-form__actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Opening PR…" : "Open PR for review"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/admin/agents")}
          >
            Cancel
          </button>
        </div>
      </form>

      {showConfirm && (
        <Modal onClose={() => setShowConfirm(false)}>
          <div className="modal__body">
            <h2>Open pull request?</h2>
            <p>
              This will open a pull request on the repository with the new agent
              definition. The agent will be available once the PR is merged and
              CI passes.
            </p>
            <div className="modal__actions">
              <button className="btn btn-primary" onClick={confirmCreate}>
                Continue
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
